import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { FillPage } from '@/components/layout/PageContainer';
import { useIvrDetail, useUpdateIvr } from '../api';
import { IvrFlowEditor } from '../components/IvrFlowEditor';
import type {
  IvrEdge,
  IvrNode,
  IvrWireData,
} from '../types';

export function EditIvrPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const ivrId = Number(id);

  const detail = useIvrDetail(ivrId);
  const update = useUpdateIvr();

  const hydrated = useMemo(() => {
    if (!detail.data) return null;
    /**
     * The wire format stores each `flowRender` (the original RF node) so
     * round-tripping the editor is straightforward: re-read every node
     * from `nodes.{id}.flowRender`. Edges come back from the separate
     * `edgesData` string.
     */
    let nodes: IvrNode[] = [];
    try {
      const parsed: IvrWireData | { nodes?: Record<string, { flowRender?: IvrNode }> } =
        detail.data.nodeData ? JSON.parse(detail.data.nodeData) : { nodes: {} };
      const map =
        (parsed as { nodes?: Record<string, { flowRender?: IvrNode }> }).nodes ?? {};
      nodes = Object.values(map)
        .map((entry) => entry.flowRender)
        .filter((n): n is IvrNode => Boolean(n));
    } catch {
      nodes = [];
    }
    let edges: IvrEdge[] = [];
    try {
      edges = detail.data.edgesData ? JSON.parse(detail.data.edgesData) : [];
    } catch {
      edges = [];
    }
    return {
      nodes,
      edges,
      flowName: detail.data.flowName ?? '',
      isGatherNode: detail.data.is_gather_node ?? 0,
    };
  }, [detail.data]);

  if (detail.isLoading || !hydrated) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading IVR…
      </div>
    );
  }

  return (
    <FillPage>
      <IvrFlowEditor
        mode="update"
        ivrId={ivrId}
        initialNodes={hydrated.nodes}
        initialEdges={hydrated.edges}
        initialFlowName={hydrated.flowName}
        initialIsGatherNode={hydrated.isGatherNode}
        isPending={update.isPending}
        onSave={async (req) => {
          try {
            const res = await update.mutateAsync({ ...req, ivrId });
            if (res?.message === 'Parameters Incorrect') {
              toast.error(res.message);
              return;
            }
            toast.success(res?.message ?? 'IVR updated');
            navigate('/ivr-list');
          } catch {
            toast.error('Could not update the IVR.');
          }
        }}
      />
    </FillPage>
  );
}
